import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  PlayCircle,
} from "lucide-react";

const SchedulePage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchActivities();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const loadedProjects = response.data || [];
      setProjects(loadedProjects);
      if (loadedProjects.length > 0) {
        setSelectedProject(loadedProjects[0].id);
      }
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(
        `${API}/projects/${selectedProject}/activities`
      );
      setActivities(response.data || []);
    } catch (error) {
      toast.error("Failed to load activities");
    }
  };

  const handleStatusChange = async (activityId, newStatus) => {
    try {
      const response = await axios.put(
        `${API}/projects/${selectedProject}/activities/${activityId}`,
        { status: newStatus }
      );
      setActivities(
        activities.map((a) =>
          a.id === activityId ? response.data : a
        )
      );
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "complete":
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
      case "in_progress":
        return <PlayCircle className="h-5 w-5 text-primary" />;
      case "blocked":
        return <AlertTriangle className="h-5 w-5 text-destructive" />;
      case "ready":
        return <Clock className="h-5 w-5 text-amber-400" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "complete":
        return "bg-emerald-500/20 border-emerald-500/50";
      case "in_progress":
        return "bg-primary/20 border-primary/50";
      case "blocked":
        return "bg-destructive/20 border-destructive/50";
      case "ready":
        return "bg-amber-500/20 border-amber-500/50";
      default:
        return "bg-muted/50 border-muted";
    }
  };

  // Group activities by status for Kanban view
  const activityGroups = {
    not_started: activities.filter((a) => a.status === "not_started"),
    ready: activities.filter((a) => a.status === "ready"),
    in_progress: activities.filter((a) => a.status === "in_progress"),
    blocked: activities.filter((a) => a.status === "blocked"),
    complete: activities.filter((a) => a.status === "complete"),
  };

  const statusLabels = {
    not_started: "Not Started",
    ready: "Ready",
    in_progress: "In Progress",
    blocked: "Blocked",
    complete: "Complete",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Schedule</h1>
          <p className="text-muted-foreground mt-1">
            Manage activities and track progress
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[250px] bg-muted/50" data-testid="project-select">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.length > 0 ? projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              )) : <SelectItem value="none" disabled>No projects yet</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(activityGroups).map(([status, items]) => (
          <Card key={status} className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground capitalize">
                    {statusLabels[status]}
                  </p>
                  <p className="text-2xl font-bold mt-1">{items.length}</p>
                </div>
                {getStatusIcon(status)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Kanban Board */}
      {selectedProject ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-x-auto">
          {Object.entries(activityGroups).map(([status, items]) => (
            <div key={status} className="min-w-[250px]">
              <div className="flex items-center gap-2 mb-4">
                {getStatusIcon(status)}
                <h3 className="font-semibold capitalize">{statusLabels[status]}</h3>
                <span className="text-sm text-muted-foreground">({items.length})</span>
              </div>
              <div className="space-y-3">
                {items.map((activity) => (
                  <Card
                    key={activity.id}
                    className={`border ${getStatusColor(activity.status)} hover:border-primary/50 transition-colors cursor-pointer`}
                    data-testid={`activity-card-${activity.id}`}
                  >
                    <CardContent className="p-4">
                      <h4 className="font-medium text-sm mb-2">{activity.name}</h4>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="capitalize">{activity.activity_type}</span>
                        <span className="font-mono">
                          {activity.duration_hours_planned || ((activity.duration_days || 1) * 8)}h
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <Select
                          value={activity.status}
                          onValueChange={(value) =>
                            handleStatusChange(activity.id, value)
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_started">Not Started</SelectItem>
                            <SelectItem value="ready">Ready</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                            <SelectItem value="complete">Complete</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {items.length === 0 && (
                  <div className="p-4 border border-dashed border-border rounded-lg text-center text-sm text-muted-foreground">
                    No activities
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="stat-card">
          <CardContent className="p-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">No project selected</h3>
            <p className="text-muted-foreground">
              Select a project to view its schedule and activities
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SchedulePage;
